/**
 * PATH: packages/ui/src/components/settings/email-accounts-tab/EmailAccountCard.tsx
 * PURPOSE: Renders a single connected email account with metadata, access grants, and actions
 * WHY: Extracted from EmailAccountsTab.tsx to keep the orchestrator under 300 lines
 *
 * FLOW:
 *   ┌──────────────────┐     ┌─────────────────┐     ┌──────────────┐
 *   │ EmailAccountData │  →  │ EmailAccountCard │  →  │ Card UI      │
 *   └──────────────────┘     └─────────────────┘     └──────────────┘
 *
 * DEPENDENCIES:
 *   - lucide-react: icons
 *   - ../../lib/utils: cn utility
 *   - ./email-accounts-tab-types: EmailAccountData, PROVIDER_CONFIG, formatEmailDate
 */

import {
  Mail,
  Trash2,
  Users,
  RefreshCw,
  AlertCircle,
  Check,
  Clock,
  Lock,
  User,
  Shield,
  ShieldCheck,
} from 'lucide-react'
import { cn } from '../../../lib/utils'
import { PROVIDER_CONFIG, formatEmailDate, resolveOwnerName } from './email-accounts-tab-types'
import type {
  AccessLevel,
  EmailAccountData,
  OrgMemberOption,
} from './email-accounts-tab-types'
import { SharedAccessEditor } from './SharedAccessEditor'

// =============================================================================
// TYPES
// =============================================================================

export interface EmailAccountCardProps {
  account: EmailAccountData
  onToggleSync: (accountId: number, enabled: boolean) => void
  onDeactivate: (accountId: number) => void
  /** Quotech user id of the viewer (string). The privacy toggle is
   *  owner-only, so it only renders when this matches the mailbox owner. */
  currentUserId?: string | null
  /** Flip the mailbox between private (personal) and shared. When omitted
   *  the privacy control is hidden. */
  onSetPrivacy?: (accountId: number, makePrivate: boolean) => void
  /** Toggle the per-mailbox Kanban approval gate. When omitted the approval
   *  control is hidden. */
  onSetApproval?: (accountId: number, requireApproval: boolean) => void
  /** Org members pickable in the share editor (owner of a shared box only). */
  members?: OrgMemberOption[]
  /** Add a member to a shared mailbox's access list. */
  onAddGrant?: (accountId: number, userId: number, accessLevel: AccessLevel) => void
  /** Revoke a member's access to a shared mailbox. */
  onRemoveGrant?: (accountId: number, grantId: number) => void
}

/** Pure I→O: does this viewer own this mailbox? Owner id may arrive as the
 *  flat ``owner_user_id`` (number) or the nested ``owner.id`` — compare both
 *  as strings against the viewer id, which the settings layer carries as a
 *  string. */
export function isMailboxOwner(
  account: EmailAccountData,
  currentUserId?: string | null,
): boolean {
  if (currentUserId == null || currentUserId === '') return false
  const ownerId = account.owner_user_id ?? account.owner?.id
  return ownerId != null && String(ownerId) === String(currentUserId)
}

// =============================================================================
// COMPONENT
// =============================================================================

export function EmailAccountCard({
  account,
  onToggleSync,
  onDeactivate,
  currentUserId,
  onSetPrivacy,
  onSetApproval,
  members,
  onAddGrant,
  onRemoveGrant,
}: EmailAccountCardProps) {
  const providerConfig = PROVIDER_CONFIG[account.provider] || PROVIDER_CONFIG.outlook
  const isPrivate = account.account_type !== 'shared'
  const isOwner = isMailboxOwner(account, currentUserId)
  // Owner OR org admin/owner (server flag), falling back to the owner check
  // for older backends that don't send can_manage_privacy.
  const canManagePrivacy =
    !!onSetPrivacy && (account.can_manage_privacy === true || isOwner)
  // The approval toggle shares the privacy authorization (owner OR admin).
  const requireApproval = account.require_kanban_approval === true
  const approvalLocked = account.kanban_approval_locked === true
  const canManageApproval =
    !!onSetApproval && (account.can_manage_privacy === true || isOwner)
  // Sharing only matters for a SHARED mailbox, and only the owner manages it.
  const canManageSharing =
    !isPrivate && isOwner && !!onAddGrant && !!onRemoveGrant

  return (
    <div className="p-4 rounded-xl border bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
      <div className="flex items-start gap-4">
        {/* Provider icon */}
        <div className={cn('w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0', providerConfig.color)}>
          <Mail className="w-5 h-5 text-white" />
        </div>

        {/* Account info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center flex-wrap gap-2 mb-1">
            <span className="font-medium text-slate-900 dark:text-white truncate">
              {account.email_address}
            </span>
            {account.needs_reconnection ? (
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">
                <AlertCircle className="w-3 h-3 mr-1" />
                Reconnect needed
              </span>
            ) : (
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
                <Check className="w-3 h-3 mr-1" />
                Active
              </span>
            )}
            {account.account_type === 'shared' ? (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400">
                <Users className="w-3 h-3" />
                Shared
              </span>
            ) : (
              <span
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300"
                title="Private. Only you can see this mailbox"
              >
                <Lock className="w-3 h-3" />
                Private
              </span>
            )}
          </div>

          <p className="text-xs text-slate-500 dark:text-slate-400 mb-2">
            {providerConfig.name} &middot; {account.display_name}
          </p>

          {/* Metadata */}
          <div className="space-y-1 mb-3">
            <div className="flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400">
              <User className="w-3.5 h-3.5" />
              <span>Connected by {resolveOwnerName(account)}</span>
            </div>
            {account.last_sync_at && (
              <div className="flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400">
                <Clock className="w-3.5 h-3.5" />
                <span>Last synced: {formatEmailDate(account.last_sync_at)}</span>
              </div>
            )}
            {account.last_sync_error && (
              <div className="flex items-center gap-2 text-xs text-red-500">
                <AlertCircle className="w-3.5 h-3.5" />
                <span>{account.last_sync_error}</span>
              </div>
            )}
          </div>

          {/* Shared access — owner of a shared mailbox gets the full editor
              (add/remove people); everyone else sees a read-only list. */}
          {canManageSharing ? (
            <SharedAccessEditor
              account={account}
              members={members ?? []}
              onAddGrant={onAddGrant!}
              onRemoveGrant={onRemoveGrant!}
            />
          ) : (
            account.access_grants && account.access_grants.length > 0 && (
              <div className="mb-3 p-2 rounded-lg bg-slate-50 dark:bg-slate-700/50">
                <p className="text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">
                  Shared with:
                </p>
                <div className="flex flex-wrap gap-1">
                  {account.access_grants.map((grant) => (
                    <span
                      key={grant.id}
                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 text-slate-700 dark:text-slate-300"
                    >
                      {grant.user_name || grant.user_email}
                      <span className="text-slate-400">({grant.access_level})</span>
                    </span>
                  ))}
                </div>
              </div>
            )
          )}

          {/* Actions */}
          <div className="flex items-center gap-2">
            <button
              onClick={() => onToggleSync(account.id, !account.sync_enabled)}
              className={cn(
                'flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors',
                account.sync_enabled
                  ? 'bg-green-50 text-green-700 hover:bg-green-100 dark:bg-green-900/20 dark:text-green-400 dark:hover:bg-green-900/30'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200 dark:bg-slate-700 dark:text-slate-400 dark:hover:bg-slate-600'
              )}
            >
              <RefreshCw className="w-3.5 h-3.5" />
              {account.sync_enabled ? 'Sync On' : 'Sync Off'}
            </button>
            {canManagePrivacy && (
              <button
                onClick={() => onSetPrivacy?.(account.id, !isPrivate)}
                className={cn(
                  'flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors',
                  isPrivate
                    ? 'bg-slate-100 text-slate-600 hover:bg-slate-200 dark:bg-slate-700 dark:text-slate-300 dark:hover:bg-slate-600'
                    : 'bg-blue-50 text-blue-700 hover:bg-blue-100 dark:bg-blue-900/20 dark:text-blue-400 dark:hover:bg-blue-900/30'
                )}
                title={
                  isPrivate
                    ? 'Make this mailbox shared with your organisation'
                    : 'Make this mailbox private. Only you will see its emails'
                }
              >
                {isPrivate ? <Users className="w-3.5 h-3.5" /> : <Lock className="w-3.5 h-3.5" />}
                {isPrivate ? 'Make shared' : 'Make private'}
              </button>
            )}
            {canManageApproval && (
              <button
                onClick={() =>
                  !approvalLocked && onSetApproval?.(account.id, !requireApproval)
                }
                disabled={approvalLocked}
                className={cn(
                  'flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors',
                  approvalLocked && 'opacity-60 cursor-not-allowed',
                  requireApproval
                    ? 'bg-amber-50 text-amber-700 hover:bg-amber-100 dark:bg-amber-900/20 dark:text-amber-400 dark:hover:bg-amber-900/30'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200 dark:bg-slate-700 dark:text-slate-300 dark:hover:bg-slate-600'
                )}
                title={
                  approvalLocked
                    ? 'Approval is locked on for a private mailbox. Switch it to shared to disable.'
                    : requireApproval
                      ? 'Emails wait for manual approval before filing to a board. Click to turn approval off.'
                      : 'Emails auto-file to boards. Click to require manual approval first.'
                }
              >
                {requireApproval ? (
                  <ShieldCheck className="w-3.5 h-3.5" />
                ) : (
                  <Shield className="w-3.5 h-3.5" />
                )}
                {requireApproval ? 'Approval On' : 'Approval Off'}
              </button>
            )}
            <button
              onClick={() => onDeactivate(account.id)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
            >
              <Trash2 className="w-3.5 h-3.5" />
              Disconnect
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
